import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme, alerttype }) => {
  const getBackgroundColor = () => {
    switch (alerttype) {
      case 'success':
        return theme.palette.success.main;
      case 'danger':
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: theme.spacing(1),
      minWidth: 400,
      maxWidth: 500,
    },
    '& .MuiDialogTitle-root': {
      backgroundColor: getBackgroundColor(),
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      borderRadius: '16px 16px 0 0',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(2),
      gap: theme.spacing(1),
    },
  };
});

const getIcon = (type) => {
  const iconProps = { sx: { fontSize: 24 } };
  
  switch (type) {
    case 'success':
      return <CheckCircle {...iconProps} />;
    case 'danger':
    case 'error':
      return <Error {...iconProps} />;
    case 'warning':
      return <Warning {...iconProps} />;
    case 'info':
      return <Info {...iconProps} />;
    default:
      return <Info {...iconProps} />;
  }
};

const getTitle = (type) => {
  switch (type) {
    case 'success':
      return 'Success';
    case 'danger':
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    default:
      return 'Alert';
  }
};

const CustomAlert = ({
  open,
  onClose,
  onConfirm,
  text,
  type = 'info',
  title,
  showCancel = true,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonColor,
  cancelButtonColor,
}) => {
  const theme = useTheme();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const getConfirmButtonColor = () => {
    if (confirmButtonColor) return confirmButtonColor;
    
    switch (type) {
      case 'success':
        return 'success';
      case 'danger':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      alerttype={type}
      aria-labelledby="custom-alert-dialog-title"
      aria-describedby="custom-alert-dialog-description"
    >
      <DialogTitle id="custom-alert-dialog-title">
        {getIcon(type)}
        <Typography variant="h6" component="span" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
          {title || getTitle(type)}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText
          id="custom-alert-dialog-description"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: 1.6,
          }}
        >
          {text}
        </DialogContentText>
      </DialogContent>
      
      <DialogActions>
        {showCancel && (
          <Button
            onClick={handleCancel}
            variant="outlined"
            color={cancelButtonColor || 'inherit'}
            sx={{
              fontFamily: 'Poppins',
              textTransform: 'none',
              borderRadius: 8,
              px: 3,
            }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={getConfirmButtonColor()}
          sx={{
            fontFamily: 'Poppins',
            textTransform: 'none',
            borderRadius: 8,
            px: 3,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default CustomAlert;